import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import spotifyService from '../../services/spotifyService';
import SpotifyArtistsConfig from '../../components/SpotifyArtistsConfig';
import spotifyArtistsAgent from '../../services/spotifyArtistsAgent';
import usageTracker from '../../utils/usageTracker';

// Debounce function to prevent rapid API calls
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const PlaylistingApp = () => {
  const navigate = useNavigate();
  const [playlistData, setPlaylistData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(null);
  
  // Search and discovery state
  const [viewMode, setViewMode] = useState('discoveries'); // Always show discoveries now
  const [searchQuery, setSearchQuery] = useState('');
  const [catalogTracks, setCatalogTracks] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogMonitoringLoading, setCatalogMonitoringLoading] = useState(false);
  
  // Search dropdown state (like charting/social listening)
  const [showSearch, setShowSearch] = useState(false);
  
  // Playlist images state
  const [playlistImages, setPlaylistImages] = useState({}); // Maps playlist ID to images array
  const [imagesLoading, setImagesLoading] = useState(false);
  
  // Discovery history and persistence
  const [discoveryHistory, setDiscoveryHistory] = useState([]);
  const [selectedDiscovery, setSelectedDiscovery] = useState(null);
  const [refreshingTrack, setRefreshingTrack] = useState(null);
  // selectedDiscoveryTrack merged with selectedDiscovery for consistency
  
  // Spotify for Artists Agent state
  // Agent status is now managed by the SpotifyArtistsConfig component
  const [useAgent, setUseAgent] = useState(true); // Preference to use agent when available

  // NEW: Multi-platform support state
  const [multiPlatformEnabled, setMultiPlatformEnabled] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['spotify']); // Start with Spotify only for compatibility

  // Platform configuration based on SongStats sources (reduced to core platforms)
  const availablePlatforms = [
    { id: 'spotify', name: 'Spotify', color: '#1DB954' },
    { id: 'apple_music', name: 'Apple Music', color: '#FA233B' },
    { id: 'amazon', name: 'Amazon Music', color: '#FF9900' },
    { id: 'deezer', name: 'Deezer', color: '#FEAA2D' },
    { id: 'youtube', name: 'YouTube Music', color: '#FF0000' },
    { id: 'soundcloud', name: 'SoundCloud', color: '#FF5500' }
    // Commented out platforms for future reference:
    // { id: 'tiktok', name: 'TikTok', color: '#FE2C55' },
    // { id: 'shazam', name: 'Shazam', color: '#0088FF' },
    // { id: 'tracklist', name: '1001Tracklists', color: '#FF6B00' },
    // { id: 'beatport', name: 'Beatport', color: '#01FF95' },
    // { id: 'traxsource', name: 'Traxsource', color: '#F15A24' },
    // { id: 'itunes', name: 'iTunes', color: '#FA233B' },
    // { id: 'tidal', name: 'Tidal', color: '#000000' }
  ];
  
  // PLAYLIST SORTING & HIGHLIGHTING UPDATES
  // ======================================= 
  // 1. Changed default sorting from 'dateAdded' to 'followers' to prioritize biggest playlists
  // 2. Official playlist highlighting uses green (#22C55E) for clear visual distinction
  // 3. Official playlists are correctly identified by curator being "Spotify"
  // 4. This ensures most valuable playlists are shown first for better user experience
  
  // Helper function to determine if a playlist is official (curated by Spotify)
  const isOfficialPlaylist = (playlist) => {
    return playlist.curator && playlist.curator.toLowerCase().includes('spotify');
  };

  // Function to fetch playlist images from Spotify API
  const fetchPlaylistImages = async (playlists) => {
    if (!playlists || playlists.length === 0) return;
    
    console.log('🖼️ Fetching images for', playlists.length, 'playlists');
    setImagesLoading(true);
    
    try {
      const imageMap = await spotifyService.batchGetPlaylistImages(playlists);
      setPlaylistImages(prev => ({ ...prev, ...imageMap }));
      console.log('✅ Updated playlist images state with', Object.keys(imageMap).length, 'playlists');
    } catch (error) {
      console.error('❌ Error fetching playlist images:', error);
    } finally {
      setImagesLoading(false);
    }
  };

  // Memoized function to get playlist image URL for better performance
  const getPlaylistImageUrl = useMemo(() => {
    return (playlist) => {
      if (!playlist) return null;
      
      const playlistId = playlist.id || playlist.playlist_id || spotifyService.extractPlaylistId(playlist.playlist_url || '');
      if (!playlistId) return null;
      
      const images = playlistImages[playlistId];
      
      if (images && Array.isArray(images) && images.length > 0) {
        // Return the first (usually highest quality) image
        return images[0]?.url || null;
      }
      
      return null;
    };
  }, [playlistImages]);

  // Sorting state for monitoring data
  const [sortBy, setSortBy] = useState('followers'); // Changed from 'dateAdded' to 'followers' - prioritize biggest playlists by default
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  // Temporarily hide certain tabs (set to true to show them again for testing)
  const showHiddenTabs = false;
  const showOfficialTabs = false; // Hide Official/Unofficial tabs for now



  // Get cache key for longer-term storage (1 month)
  const getCacheKey = () => {
    return `playlist-discoveries-cache`;
  };

  // Get today's cache key (kept for backward compatibility)
  const getTodaysCacheKey = () => {
    const today = new Date().toISOString().split('T')[0];
    return `playlist-discoveries-${today}`;
  };

  // Lightweight cache loading - no API calls, only localStorage
  const loadCachedDiscoveriesLightweight = () => {
    console.log('📚 Loading lightweight cached discoveries (localStorage only)...');
    
    // Try 1-month cache first
    const cached = localStorage.getItem(getCacheKey());
    if (cached) {
      try {
        const cache = JSON.parse(cached);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        // Filter out discoveries older than 1 month
        const validDiscoveries = cache.discoveries.filter(discovery => 
          new Date(discovery.searchDate) > oneMonthAgo
        );
        
        if (validDiscoveries.length > 0) {
          setDiscoveryHistory(validDiscoveries);
          
          // Flatten all playlists from all discoveries for display
          const allPlaylists = validDiscoveries.flatMap(discovery => 
            discovery.playlists.map(playlist => ({
              ...playlist,
              discovery_date: discovery.searchDate,
              search_track: discovery.track
            }))
          );
          setPlaylistData(allPlaylists);
          console.log('📚 Loaded', validDiscoveries.length, 'cached discoveries (lightweight) with', allPlaylists.length, 'total playlists');
          return;
        }
      } catch (error) {
        console.error('❌ Error loading cached discoveries:', error);
      }
    }
    
    // Fallback to today's cache
    loadTodaysDiscoveries();
  };

  // Load cached discoveries with 1-month expiration and database fallback (USER-INITIATED ONLY)
  const loadCachedDiscoveries = async () => {
    // First try to load from database for cross-device persistence
    try {
      console.log('📚 Loading discoveries from database...');
      const databaseDiscoveries = await spotifyService.loadDiscoveriesFromDatabase();
      
      if (databaseDiscoveries && databaseDiscoveries.length > 0) {
        // Transform database data to match our format
        const discoveries = databaseDiscoveries.map(dbDiscovery => ({
          track: dbDiscovery.track,
          playlists: dbDiscovery.playlists,
          searchDate: dbDiscovery.search_date,
          trackKey: dbDiscovery.track_key
        }));
        
        setDiscoveryHistory(discoveries);
        
        // Flatten all playlists from all discoveries for display
        const allPlaylists = discoveries.flatMap(discovery => 
          discovery.playlists.map(playlist => ({
            ...playlist,
            discovery_date: discovery.searchDate,
            search_track: discovery.track
          }))
        );
        setPlaylistData(allPlaylists);
        console.log('📚 Loaded', discoveries.length, 'discoveries from database with', allPlaylists.length, 'total playlists');
        return; // Successfully loaded from database, no need to check localStorage
      }
    } catch (error) {
      console.warn('⚠️ Failed to load from database, falling back to localStorage:', error);
    }
    
    // Fallback to localStorage if database fails or is empty
    const cached = localStorage.getItem(getCacheKey());
    if (cached) {
      try {
        const cache = JSON.parse(cached);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        // Filter out discoveries older than 1 month
        const validDiscoveries = cache.discoveries.filter(discovery => 
          new Date(discovery.searchDate) > oneMonthAgo
        );
        
        if (validDiscoveries.length !== cache.discoveries.length) {
          // Update cache if we removed old entries
          localStorage.setItem(getCacheKey(), JSON.stringify({
            discoveries: validDiscoveries,
            lastUpdated: new Date().toISOString()
          }));
        }
        
        setDiscoveryHistory(validDiscoveries);
        
        // Flatten all playlists from all discoveries for display
        const allPlaylists = validDiscoveries.flatMap(discovery => 
          discovery.playlists.map(playlist => ({
            ...playlist,
            discovery_date: discovery.searchDate,
            search_track: discovery.track
          }))
        );
        setPlaylistData(allPlaylists);
        console.log('📚 Loaded', validDiscoveries.length, 'cached discoveries (localStorage fallback) with', allPlaylists.length, 'total playlists');
      } catch (error) {
        console.error('❌ Error loading cached discoveries:', error);
        // Fallback to today's cache for backward compatibility
        loadTodaysDiscoveries();
      }
    } else {
      // Fallback to today's cache for backward compatibility
      loadTodaysDiscoveries();
    }
  };

  useEffect(() => {
    console.log('🚀 PlaylistingApp mounted');
    
    // Load catalog on mount (essential for functionality)
    loadCatalog(true); // User-initiated flag to ensure it loads
    
    // Only load lightweight cached data on mount - no other API calls
    loadCachedDiscoveriesLightweight();
  }, []);

  // Fetch playlist images when playlist data changes (DISABLED FOR RATE LIMITING)
  // Playlist images are now loaded on-demand when user hovers or clicks
  useEffect(() => {
    // RATE LIMITING FIX: Don't auto-fetch images on data change
    // This was causing multiple Spotify API calls and token requests
    // Images will be loaded lazily when needed
    
    if (playlistData.length > 0) {
      console.log('📊 Playlist data updated:', playlistData.length, 'playlists loaded (images will load on-demand)');
    }
  }, [playlistData]);

  const loadCatalog = async (userInitiated = false) => {
    if (!userInitiated) {
      console.log('📚 Catalog loading skipped - only user-initiated loading allowed to prevent rate limiting');
      return;
    }
    
    console.log('📚 Loading user catalog (user-initiated)...');
    setCatalogLoading(true);
    try {
      const catalog = await spotifyService.getUserCatalog();
      setCatalogTracks(catalog);
      console.log('📚 Loaded', catalog.length, 'tracks from catalog');
    } catch (error) {
      console.error('❌ Failed to load catalog:', error);
      setError('Failed to load your catalog');
    } finally {
      setCatalogLoading(false);
    }
  };

  const loadTodaysDiscoveries = () => {
    const cached = localStorage.getItem(getTodaysCacheKey());
    if (cached) {
      try {
        const discoveries = JSON.parse(cached);
        setDiscoveryHistory(discoveries);
        
        // Flatten all playlists from all discoveries for display
        const allPlaylists = discoveries.flatMap(discovery => 
          discovery.playlists.map(playlist => ({
            ...playlist,
            discovery_date: discovery.searchDate,
            search_track: discovery.track
          }))
        );
        setPlaylistData(allPlaylists);
        console.log('📚 Loaded', discoveries.length, 'cached discoveries with', allPlaylists.length, 'total playlists');
      } catch (error) {
        console.error('❌ Error loading cached discoveries:', error);
      }
    }
  };

  const saveDiscovery = async (track, playlists) => {
    const discovery = {
      track,
      playlists,
      searchDate: new Date().toISOString(),
      trackKey: `${track.song_title || track.name}-${track.artist_name || track.artist}`
    };

    // Save to database first for cross-device persistence
    try {
      console.log('💾 Saving discovery to database...');
      await spotifyService.saveDiscoveryToDatabase(track, playlists);
    } catch (error) {
      console.warn('⚠️ Failed to save to database, continuing with localStorage:', error);
    }

    // Load existing cache
    const cached = localStorage.getItem(getCacheKey());
    let existingDiscoveries = [];
    
    if (cached) {
      try {
        const cache = JSON.parse(cached);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        // Keep only discoveries from the last month
        existingDiscoveries = cache.discoveries.filter(d => 
          new Date(d.searchDate) > oneMonthAgo
        );
      } catch (error) {
        console.error('❌ Error parsing existing cache:', error);
      }
    }

    // Update or add discovery (remove old version of same track if exists)
    const updatedHistory = [...existingDiscoveries.filter(d => d.trackKey !== discovery.trackKey), discovery];
    setDiscoveryHistory(updatedHistory);
    
    // Save to 1-month cache (as backup)
    localStorage.setItem(getCacheKey(), JSON.stringify({
      discoveries: updatedHistory,
      lastUpdated: new Date().toISOString()
    }));
    
    // Also save to today's cache for backward compatibility
    localStorage.setItem(getTodaysCacheKey(), JSON.stringify(updatedHistory));
    
    // Update display data
    const allPlaylists = updatedHistory.flatMap(d => 
      d.playlists.map(playlist => ({
        ...playlist,
        discovery_date: d.searchDate,
        search_track: d.track
      }))
    );
    setPlaylistData(allPlaylists);
    
    console.log(`📚 Saved discovery (database + localStorage). Total discoveries: ${updatedHistory.length}`);
  };

  const searchCatalog = (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = catalogTracks.filter(track => {
      const trackName = (track.song_title || track.name || '').toLowerCase();
      const artistName = (track.artist_name || track.artist || '').toLowerCase();
      const searchTerm = query.toLowerCase();
      
      return trackName.includes(searchTerm) || artistName.includes(searchTerm);
    });

    setSearchResults(results);
  };

  // Track monitoring functions
  const startCatalogMonitoring = async () => {
    console.log('🎯 Starting catalog monitoring...');
    console.log('🌐 Multi-platform enabled:', multiPlatformEnabled);
    console.log('🎯 Selected platforms:', selectedPlatforms);
    
    setCatalogMonitoringLoading(true);
    setError(null);

    try {
      // Load catalog first if not already loaded (user-initiated)
      if (catalogTracks.length === 0) {
        console.log('📚 Catalog not loaded, loading now...');
        await loadCatalog(true);
      }
      
      console.log(`📊 Monitoring ${catalogTracks.length} tracks from catalog`);
      
      if (catalogTracks.length === 0) {
        setError('No tracks in catalog to monitor. Please add some tracks first.');
        return;
      }
      
      // Get monitoring data for all catalog tracks with multi-platform support
      const monitoringResults = await spotifyService.getBulkTrackMonitoring(catalogTracks, { 
        limit: multiPlatformEnabled ? 75 : 100, // Reduced to respect 100 max API limit: 75 for multi-platform, 100 for single-platform
        batchSize: 3,
        multiPlatform: multiPlatformEnabled,
        platforms: selectedPlatforms
      });
      
      console.log('📊 Monitoring results:', monitoringResults);
      
      // Organize monitoring data by track
      const organizedData = {};
      let allPlaylists = [];
      let totalMetrics = {
        totalTracks: 0,
        totalPlaylists: 0,
        editorialCount: 0,
        recentAdditions: 0,
        totalReach: 0,
        platformBreakdown: {}
      };
      
      monitoringResults.forEach(result => {
        if (result.success && result.monitoring.playlists.length > 0) {
          const trackKey = `${result.track.song_title}-${result.track.artist_name}`;
          organizedData[trackKey] = result.monitoring;
          
          // Add to all playlists for display
          allPlaylists = [...allPlaylists, ...result.monitoring.playlists];
          
          // Accumulate metrics
          totalMetrics.totalTracks++;
          const metrics = result.monitoring.metrics || {};
          totalMetrics.totalPlaylists += metrics.totalPlaylists || 0;
          totalMetrics.editorialCount += metrics.editorialCount || 0;
          totalMetrics.recentAdditions += metrics.recentAdditions || 0;
          totalMetrics.totalReach += metrics.totalReach || 0;
          
          // Accumulate platform breakdown
          if (metrics.platformBreakdown) {
            Object.keys(metrics.platformBreakdown).forEach(platform => {
              if (!totalMetrics.platformBreakdown[platform]) {
                totalMetrics.platformBreakdown[platform] = { count: 0, followers: 0 };
              }
              totalMetrics.platformBreakdown[platform].count += metrics.platformBreakdown[platform].count;
              totalMetrics.platformBreakdown[platform].followers += metrics.platformBreakdown[platform].followers;
            });
          }
        }
      });
      
      setPlaylistData(allPlaylists);
      
      const platformsSearched = multiPlatformEnabled ? selectedPlatforms.join(', ') : 'Spotify';
      console.log(`✅ Monitoring complete: ${totalMetrics.totalTracks} tracks, ${totalMetrics.totalPlaylists} playlists across ${platformsSearched}`);
      
      // Already in discoveries view since that's the only view now
      
    } catch (error) {
      console.error('❌ Error starting catalog monitoring:', error);
      setError('Failed to start catalog monitoring. Please try again.');
    } finally {
      setCatalogMonitoringLoading(false);
    }
  };

  const monitorSingleTrack = async (track) => {
    console.log(`🎯 Monitoring single track: "${track.song_title}" by ${track.artist_name}`);
    console.log('🌐 Multi-platform enabled:', multiPlatformEnabled);
    console.log('🎯 Selected platforms:', selectedPlatforms);
    
    setSelectedDiscovery(track);
    setLoading(true);
    setError(null);

    try {
      // Get real-time monitoring for this track with multi-platform support
      const monitoringResult = await spotifyService.getTrackPlaylistMonitoring(track, {
        limit: multiPlatformEnabled ? 75 : 100, // Use reduced limit for multi-platform to distribute across platforms
        multiPlatform: multiPlatformEnabled,
        platforms: selectedPlatforms
      });
      
      if (monitoringResult.playlists.length > 0) {
        console.log(`✅ Found monitoring data for "${track.song_title}":`, monitoringResult);
        
        // Use the same saveDiscovery function as search new
        await saveDiscovery(track, monitoringResult.playlists);
        
        // Create a properly formatted track object for discovery selection
        const discoveryTrack = {
          ...track,
          playlistCount: monitoringResult.playlists.length,
          lastSearched: new Date().toISOString(),
          trackKey: `${track.song_title}-${track.artist_name}`
        };
        
        setSelectedDiscovery(discoveryTrack);
        console.log(`🎯 Set selected discovery:`, discoveryTrack);
      } else {
        console.log(`❌ No monitoring data found for "${track.song_title}"`);
        setError(`No playlist placements found for "${track.song_title}" by ${track.artist_name}`);
      }
    } catch (error) {
      console.error('❌ Error monitoring track:', error);
      setError(`Failed to get monitoring data for "${track.song_title}"`);
    } finally {
      setLoading(false);
      
      // Close search area after search process is complete (success or failure)
      setShowSearch(false);
    }
  };

  // Debounced version to prevent rapid API calls
  const debouncedMonitorSingleTrack = useCallback(
    debounce(monitorSingleTrack, 1000), // 1 second debounce
    [multiPlatformEnabled, selectedPlatforms]
  );

  // Handle track selection - monitor track (search area will close after completion)
  const handleTrackSelection = (track) => {
    // Start monitoring the selected track
    debouncedMonitorSingleTrack(track);
    
    // Clear search query and results immediately so user knows they clicked
    setSearchQuery('');
    setSearchResults([]);
    // Note: Search area stays open until search completes
  };

  const searchPlaylistsForTrack = async (track) => {
    console.log(`🔍 Searching playlists for track: "${track.song_title}" by ${track.artist_name}`);
    console.log('🌐 Multi-platform enabled:', multiPlatformEnabled);
    console.log('🎯 Selected platforms:', selectedPlatforms);
    
    setSelectedDiscovery(track);
    setLoading(true);
    setError(null);

    try {
      const searchMethod = multiPlatformEnabled ? 'multi-platform search' : 'enhanced search';
      console.log(`🔍 Using ${searchMethod} for "${track.song_title}"`);
      
      const spotifyData = await spotifyService.getPlaylistPlacementsForTrackEnhanced(track, {
        limit: multiPlatformEnabled ? 75 : 100, // Use reduced limit for multi-platform to distribute across platforms
        useAgent: !multiPlatformEnabled, // Only use agent for single-platform Spotify searches
        multiPlatform: multiPlatformEnabled,
        platforms: selectedPlatforms
      });
      
      console.log('📊 Received response:', spotifyData);
      
      if (Array.isArray(spotifyData)) {
        console.log('📊 Found', spotifyData.length, 'playlist placements');
        await saveDiscovery(track, spotifyData);
        
        // Track playlisting usage
        try {
          const searchType = multiPlatformEnabled ? 'multi-platform' : 'enhanced';
          const platforms = multiPlatformEnabled ? selectedPlatforms.join(',') : 'spotify';
          await usageTracker.trackPlaylisting(searchType, platforms, spotifyData.length);
        } catch (trackingError) {
          console.error('Usage tracking failed:', trackingError);
        }
        
        // Create a properly formatted track object for discovery selection
        const discoveryTrack = {
          ...track,
          playlistCount: spotifyData.length,
          lastSearched: new Date().toISOString(),
          trackKey: `${track.song_title}-${track.artist_name}`
        };
        
        setSelectedDiscovery(discoveryTrack);
        console.log(`🎯 Set selected discovery:`, discoveryTrack);
      } else {
        console.log('❌ Invalid response format or no playlists found');
        setError(`No playlist placements found for "${track.song_title}" by ${track.artist_name}`);
      }
    } catch (error) {
      console.error('❌ Error searching for playlists:', error);
      
      // Check if error is related to agent limits
      if (error.message?.includes('503') || error.message?.includes('rate limit')) {
        setError(`Rate limit reached. Please wait a moment before searching again.`);
      } else if (error.message?.includes('No results') || error.message?.includes('returned no results')) {
        setError(`No playlist placements found for "${track.song_title}" by ${track.artist_name}`);
      } else {
        setError(`Failed to search for "${track.song_title}". Please try again.`);
      }
    } finally {
      setLoading(false);
      
      // Close search area after search process is complete (success or failure)
      setShowSearch(false);
    }
  };

  // Debounced version to prevent rapid API calls
  const debouncedSearchPlaylistsForTrack = useCallback(
    debounce(searchPlaylistsForTrack, 1000), // 1 second debounce
    [multiPlatformEnabled, selectedPlatforms]
  );

  // Search functionality for catalog
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchCatalog(query);
  };

  // Refresh discovery function
  const refreshTrackDiscovery = async (discovery) => {
    console.log('🔄 Refreshing discovery for track:', discovery.track);
    
    setRefreshingTrack(discovery.trackKey);
    
    try {
      const spotifyData = await spotifyService.getPlaylistPlacementsForTrackEnhanced(discovery.track, {
        multiPlatform: multiPlatformEnabled,
        platforms: selectedPlatforms
      });
      
      if (Array.isArray(spotifyData)) {
        await saveDiscovery(discovery.track, spotifyData);
        console.log('✅ Refreshed discovery with', spotifyData.length, 'playlist placements');
      }
    } catch (error) {
      console.error('❌ Failed to refresh discovery:', error);
      setError(`Failed to refresh "${discovery.track.song_title || discovery.track.name}"`);
    } finally {
      setRefreshingTrack(null);
    }
  };

  const clearTodaysDiscoveries = () => {
    localStorage.removeItem(getTodaysCacheKey());
    setDiscoveryHistory([]);
    setPlaylistData([]);
  };

  const getDiscoveredTracks = () => {
    return discoveryHistory.map(d => ({
      ...d.track,
      playlistCount: d.playlists.length,
      lastSearched: d.searchDate,
      trackKey: d.trackKey
    }));
  };

  const clearAllDiscoveries = () => {
    // Clear both new 1-month cache and legacy today's cache
    localStorage.removeItem(getCacheKey());
    localStorage.removeItem(getTodaysCacheKey());
    setDiscoveryHistory([]);
    setPlaylistData([]);
    console.log('🗑️ Cleared all cached discoveries');
  };

  // Enhanced playlist filtering for discoveries tab
  const filteredPlaylistData = playlistData.filter(playlist => {
    // First filter by selected discovery track if one is selected
    let matchesTrack = true;
    if (selectedDiscovery) {
      matchesTrack = playlist.track_name === selectedDiscovery.song_title && 
             playlist.artist_name === selectedDiscovery.artist_name;
    }

    // If track doesn't match, exclude it
    if (!matchesTrack) return false;

    // Then apply platform/tab filtering on top of track filtering
    if (activeTab === 'all') return true;
    if (activeTab === 'official') return isOfficialPlaylist(playlist);
    if (activeTab === 'unofficial') return !isOfficialPlaylist(playlist);
    
    // Platform-specific filtering for all supported platforms
    if (activeTab === 'spotify') return playlist.platform === 'spotify';
    if (activeTab === 'apple_music') return playlist.platform === 'apple_music';
    if (activeTab === 'amazon') return playlist.platform === 'amazon';
    if (activeTab === 'deezer') return playlist.platform === 'deezer';
    if (activeTab === 'youtube') return playlist.platform === 'youtube';
    if (activeTab === 'soundcloud') return playlist.platform === 'soundcloud';
    
    return true;
  });

  // Apply sorting to filtered data
  const sortedPlaylistData = [...filteredPlaylistData].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortBy) {
      case 'dateAdded':
        aVal = new Date(a.dateAdded || 0).getTime();
        bVal = new Date(b.dateAdded || 0).getTime();
        break;
      case 'followers':
        aVal = a.followersNumeric || parseInt((a.followers || '0').replace(/[^0-9]/g, '')) || 0;
        bVal = b.followersNumeric || parseInt((b.followers || '0').replace(/[^0-9]/g, '')) || 0;
        break;
      case 'daysInPlaylist':
        aVal = a.daysInPlaylist !== undefined ? a.daysInPlaylist : 999;
        bVal = b.daysInPlaylist !== undefined ? b.daysInPlaylist : 999;
        break;
      case 'alphabetical':
        aVal = (a.playlist_name || '').toLowerCase();
        bVal = (b.playlist_name || '').toLowerCase();
        return sortOrder === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      default:
        return 0;
    }
    
    return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
  });

  // Utility functions
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const tabs = [
    { id: 'all', label: 'All' },
    ...(showOfficialTabs ? [
    { id: 'official', label: 'Official' },
      { id: 'unofficial', label: 'Unofficial' }
    ] : []),
    { id: 'spotify', label: 'Spotify' },
    { id: 'apple_music', label: 'Apple' },
    { id: 'amazon', label: 'Amazon' },
    { id: 'deezer', label: 'Deezer' },
    { id: 'youtube', label: 'YouTube' },
    { id: 'soundcloud', label: 'SoundCloud' }
    // Commented out for future reference:
    // { id: 'tiktok', label: 'TikTok' },
    // { id: 'shazam', label: 'Shazam' },
    // { id: 'tracklist', label: '1001Tracklists' },
    // { id: 'beatport', label: 'Beatport' },
    // { id: 'traxsource', label: 'Traxsource' },
    // { id: 'itunes', label: 'iTunes' },
    // { id: 'tidal', label: 'Tidal' }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} minutes ago`;
      }
      return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
    } else if (diffInDays === 1) {
      return '1 day ago';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else if (diffInWeeks === 1) {
      return '1 week ago';
    } else if (diffInWeeks < 4) {
      return `${diffInWeeks} weeks ago`;
    } else if (diffInMonths === 1) {
      return '1 month ago';
    } else if (diffInMonths < 12) {
      return `${diffInMonths} months ago`;
    } else if (diffInYears === 1) {
      return '1 year ago';
    } else {
      return `${diffInYears} years ago`;
    }
  };



  // Enhanced function to get playlist image URL with direct URL fallback
  const getPlaylistImageUrlEnhanced = useMemo(() => {
    return (playlist) => {
      if (!playlist) return null;
      
      // First, try to get from loaded images state
      const playlistId = playlist.id || playlist.playlist_id || spotifyService.extractPlaylistId(playlist.playlist_url || '');
      if (playlistId && playlistImages[playlistId]) {
        const images = playlistImages[playlistId];
        if (images && Array.isArray(images) && images.length > 0) {
          return images[0]?.url || null;
        }
      }
      
      // Fallback to direct URL from API response
      if (playlist.playlist_image) {
        return playlist.playlist_image;
      }
      
      return null;
    };
  }, [playlistImages]);

  // Intersection observer ref for lazy loading
  const observerRef = useRef();
  const playlistRefs = useRef(new Map());
  const observedElements = useRef(new Set()); // Track which elements we're observing

  // Function to load images when playlist enters viewport
  const loadImageOnVisible = useCallback(async (playlist) => {
    if (playlist.platform !== 'spotify') return; // Only load Spotify images
    
    const playlistId = playlist.id || playlist.playlist_id || spotifyService.extractPlaylistId(playlist.playlist_url || '');
    
    // Skip if we already have the image or no ID
    if (!playlistId || playlistImages[playlistId]) {
      return;
    }
    
    console.log('🖼️ Loading image for visible playlist:', playlist.playlist_name, 'ID:', playlistId);
    
    try {
      const images = await spotifyService.getPlaylistImages(playlistId);
      if (images && images.length > 0) {
        console.log('✅ Successfully loaded image for:', playlist.playlist_name);
        setPlaylistImages(prev => ({ 
          ...prev, 
          [playlistId]: images 
        }));
      } else {
        console.log('❌ No images returned for:', playlist.playlist_name);
      }
    } catch (error) {
      console.error('❌ Error loading playlist image:', error);
    }
  }, [playlistImages]);

  // Predictive loading: load next 3 playlists when current one becomes visible
  const loadNextThreePlaylists = useCallback(async (currentPlaylist) => {
    if (!currentPlaylist || currentPlaylist.platform !== 'spotify') return;

    // Find current playlist index in the sorted data
    const currentIndex = sortedPlaylistData.findIndex(p => 
      (p.id || p.playlist_id) === (currentPlaylist.id || currentPlaylist.playlist_id)
    );

    if (currentIndex === -1) return;

    console.log('🚀 Predictive loading: Loading next 3 playlists after', currentPlaylist.playlist_name);

    // Load the next 3 Spotify playlists
    const nextPlaylists = sortedPlaylistData
      .slice(currentIndex + 1, currentIndex + 4) // Get next 3
      .filter(playlist => playlist.platform === 'spotify'); // Only Spotify

    // Load images for next playlists in parallel
    const loadPromises = nextPlaylists.map(playlist => {
      const playlistId = playlist.id || playlist.playlist_id || 
                        spotifyService.extractPlaylistId(playlist.playlist_url || '');
      
      // Skip if already loaded
      if (!playlistId || playlistImages[playlistId]) return Promise.resolve();
      
      console.log('📈 Predictively loading:', playlist.playlist_name);
      return loadImageOnVisible(playlist);
    });

    await Promise.all(loadPromises);
  }, [sortedPlaylistData, playlistImages, loadImageOnVisible]);

  // Set up intersection observer for lazy loading
  useEffect(() => {
    console.log('🔍 Setting up intersection observer...');
    
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    observerRef.current = new IntersectionObserver(
      (entries) => {
        console.log('👀 Intersection observer triggered with', entries.length, 'entries');
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            console.log('🎯 Element is intersecting:', entry.target);
            const playlistData = entry.target.playlistData;
            if (playlistData) {
              console.log('📊 Found playlist data:', playlistData.playlist_name);
              
              // Load current playlist image
              loadImageOnVisible(playlistData);
              
              // Also predictively load next 3 playlists
              loadNextThreePlaylists(playlistData);
              
              // Stop observing this element once we've loaded its image
              observerRef.current.unobserve(entry.target);
              observedElements.current.delete(entry.target);
            } else {
              console.warn('⚠️ No playlist data found on intersecting element');
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '500px', // Even more aggressive - start loading 500px before
        threshold: 0.01 // Ultra-sensitive - trigger when just 1% approaches viewport
      }
    );
    console.log('✅ Intersection observer created with ultra-aggressive settings');

    return () => {
      if (observerRef.current) {
        console.log('🧹 Cleaning up intersection observer');
        observerRef.current.disconnect();
        observedElements.current.clear();
      }
    };
  }, []); // Remove loadImageOnVisible dependency to prevent recreating observer

  // Function to register playlist element with intersection observer
  const registerPlaylistElement = useCallback((element, playlist) => {
    if (!element || !playlist) {
      return;
    }
    
    // Store playlist data directly on element
    element.playlistData = playlist;
    element.setAttribute('data-playlist-fallback', 'true'); // For fallback mechanism
    
    // Only observe Spotify playlists
    if (playlist.platform !== 'spotify') {
      return;
    }
    
    console.log('📝 Registering Spotify playlist for observation:', playlist.playlist_name);
    
    // Observe immediately if observer is ready, otherwise try after a short delay
    if (observerRef.current) {
      observerRef.current.observe(element);
      observedElements.current.add(element);
      console.log('✅ Immediately observing:', playlist.playlist_name);
    } else {
      setTimeout(() => {
        if (observerRef.current && element.playlistData) {
          console.log('📝 Delayed registration for:', playlist.playlist_name);
          observerRef.current.observe(element);
          observedElements.current.add(element);
        }
      }, 500);
    }
  }, []);

  if (loading && selectedDiscovery) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
          <div className="w-full px-4">
            <div className="text-white text-[16px] font-bold mb-2 text-left">
              Searching for "{selectedDiscovery.song_title || selectedDiscovery.name}"
            </div>
              <div className="text-[rgba(255,255,255,0.7)] text-[12px] mb-4 max-w-[300px]">
                {loadingProgress || 'Discovering playlists across Spotify...'}
              </div>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5856D6]"></div>
              </div>
            </div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Playlisting</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </button>
              <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
            </div>
          </div>

          {/* Search Dropdown */}
          {showSearch && (
            <div className="px-4 mb-6">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-[#5856D6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-white text-[12px] font-semibold">Real-Time Playlist Monitoring</span>
              </div>
                <p className="text-[rgba(255,255,255,0.7)] text-[11px] mb-4">
                  Search for a track from your catalog to monitor its real-time playlist placements with 100% accuracy
                </p>

                {/* Multi-Platform Toggle */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-[12px] font-semibold">Multi-Platform Search</span>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={multiPlatformEnabled}
                        onChange={(e) => {
                          setMultiPlatformEnabled(e.target.checked);
                          if (e.target.checked && selectedPlatforms.length === 1 && selectedPlatforms[0] === 'spotify') {
                            // When enabling multi-platform, add all core platforms since selection UI is hidden
                            setSelectedPlatforms(['spotify', 'apple_music', 'amazon', 'youtube', 'deezer', 'soundcloud']);
                          }
                        }}
                        className="sr-only"
                      />
                      <div className={`relative w-8 h-4 rounded-full transition-colors ${
                        multiPlatformEnabled ? 'bg-[#5856D6]' : 'bg-[rgba(255,255,255,0.2)]'
                      }`}>
                        <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                          multiPlatformEnabled ? 'translate-x-4' : ''
                        }`}></div>
                      </div>
                    </label>
                  </div>
                  <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-3">
                    {multiPlatformEnabled 
                      ? 'Search across multiple streaming platforms for comprehensive playlist coverage'
                      : 'Search Spotify only (fastest, most comprehensive for Spotify playlists)'
                    }
                  </p>
                  

                </div>

              {/* Search Input */}
              <div className="relative mb-4">
                <input
                  type="text"
                    placeholder="Search for a song or artist to monitor..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                    className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#5856D6]"
                />
                  <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[rgba(255,255,255,0.5)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

                {/* Error display */}
                {error && (
                  <div className="mb-4 p-3 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-lg">
                    <div className="text-[#EF4444] text-[12px]">
                      {error}
                    </div>
                    <button
                      onClick={() => setError(null)}
                      className="mt-2 text-[rgba(239,68,68,0.7)] text-[10px] hover:text-[#EF4444]"
                    >
                      Dismiss
                    </button>
                  </div>
                )}

              {/* Catalog Loading */}
              {catalogLoading && (
                <div className="py-4">
                  <div className="text-[rgba(255,255,255,0.7)] text-[12px]">Loading your catalog...</div>
                </div>
              )}

              {/* Search Results */}
              <div className="max-h-[300px] overflow-y-auto">
                {/* Only show tracks when searching */}
                {searchQuery && searchResults.length > 0 && (
                  <div className="space-y-2">
                    {searchResults.map((track, index) => (
                      <div
                        key={index}
                          onClick={() => handleTrackSelection(track)}
                        className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 hover:border-[#5856D6] hover:bg-[rgba(88,86,214,0.05)] transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-[40px] h-[40px] flex-shrink-0 rounded-lg overflow-hidden relative">
                            {track.artwork_url || track.album_artwork ? (
                              <img 
                                src={track.artwork_url || track.album_artwork} 
                                alt={track.song_title || track.name}
                                className="w-[40px] h-[40px] object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className="w-full h-full bg-gradient-to-br from-[#5856D6] to-[#7b69ff] flex items-center justify-center absolute inset-0" 
                              style={{display: track.artwork_url || track.album_artwork ? 'none' : 'flex'}}
                            >
                              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white text-[14px] font-normal text-left">
                              {track.song_title || track.name || 'Unknown Track'}
                            </h3>
                            <p className="text-[rgba(255,255,255,0.5)] text-[12px] text-left">
                              by {track.artist_name || track.artist || 'Unknown Artist'}
                            </p>
                            {track.album_name && (
                              <p className="text-[rgba(255,255,255,0.4)] text-[11px] text-left">
                                {track.album_name}
                              </p>
                            )}
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="bg-[rgba(34,197,94,0.2)] px-2 py-1 rounded text-[10px] text-green-400 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                Monitor
                          </div>
                          <svg className="w-4 h-4 text-[rgba(255,255,255,0.5)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                            </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchQuery && searchResults.length === 0 && !catalogLoading && (
                  <div className="py-8">
                    <div className="text-[rgba(255,255,255,0.5)] text-[12px] text-left">
                      No tracks found matching "{searchQuery}"
                    </div>
                  </div>
                )}

                {!searchQuery && !catalogLoading && (
                  <div className="py-8">
                    <div className="text-[rgba(255,255,255,0.5)] text-[12px] mb-2 text-left">
                      {catalogTracks.length > 0 
                        ? `Search your ${catalogTracks.length} catalog tracks` 
                        : 'No tracks in your catalog yet'
                      }
                    </div>
                    <div className="text-[rgba(255,255,255,0.4)] text-[11px] text-left">
                      {catalogTracks.length > 0 
                        ? 'Start typing to search for a specific track to monitor'
                        : 'Add some tracks to your catalog to start monitoring playlist placements'
                      }
                    </div>
                  </div>
                )}
              </div>

                {/* Monitor All Button */}
              <div className="mt-6 pt-4 border-t border-[rgba(255,255,255,0.1)]">
                <button 
                    onClick={startCatalogMonitoring}
                    disabled={catalogMonitoringLoading}
                    className="w-full bg-[#1A1A1A] border border-[#333] text-white py-3 rounded text-[12px] font-semibold hover:bg-[#2A2A2A] transition-colors disabled:opacity-50"
                >
                    {catalogMonitoringLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Monitoring {catalogTracks.length} tracks{multiPlatformEnabled ? ` across ${selectedPlatforms.length} platforms` : ''}...
                      </div>
                    ) : (
                      <>Monitor All {catalogTracks.length} Tracks{multiPlatformEnabled ? ` (${selectedPlatforms.length} Platforms)` : ''}</>
                    )}
                </button>
                </div>
              </div>
            </div>
          )}

          {/* Agent Status Indicator (show when connected) */}
          {spotifyArtistsAgent.getStatus().isAuthenticated && (
            <div className="px-4 mb-4">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-white text-[10px] font-medium">Agent Connected</span>
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useAgent}
                      onChange={(e) => setUseAgent(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`relative w-8 h-4 rounded-full transition-colors ${
                      useAgent ? 'bg-[#5856D6]' : 'bg-[rgba(255,255,255,0.2)]'
                    }`}>
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                        useAgent ? 'translate-x-4' : ''
                      }`}></div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}





          {/* Discoveries Mode */}
          {(
            <>
              {/* Discovered Tracks Summary */}
              {discoveryHistory.length > 0 && (
                <div className="px-4 mb-4">
                  <div className="mb-4">
                                         <h2 className="text-white text-[16px] font-bold mb-2 text-left">
                       Today's Discoveries
                     </h2>
                                         <div className="flex gap-2 mb-3 overflow-x-auto scrollbar-hide">
                       {/* All Tracks Button */}
                       <button
                         onClick={() => setSelectedDiscovery(null)}
                         className={`flex-shrink-0 px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors flex items-center gap-2 ${
                           selectedDiscovery === null 
                             ? 'bg-[#5856D5] text-white' 
                             : 'bg-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.2)]'
                         }`}
                       >
                         All Tracks
                       </button>
                       
                       {/* Individual Track Tags */}
                       {getDiscoveredTracks().reverse().map((track, index) => (
                         <button
                           key={index} 
                           onClick={() => setSelectedDiscovery(selectedDiscovery?.trackKey === track.trackKey ? null : track)}
                           className={`flex-shrink-0 px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors flex items-center gap-2 ${
                             selectedDiscovery?.trackKey === track.trackKey 
                               ? 'bg-[#5856D5] text-white' 
                               : 'bg-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.2)]'
                           }`}
                         >
                           {track.song_title || track.name}
                           <span className="text-current opacity-70 text-[10px]">
                             {track.playlistCount}
                           </span>
                           <button
                             onClick={(e) => {
                               e.stopPropagation();
                               refreshTrackDiscovery(discoveryHistory.find(d => d.trackKey === track.trackKey));
                             }}
                             disabled={refreshingTrack === track.trackKey}
                             className="w-4 h-4 flex items-center justify-center rounded text-current hover:bg-[rgba(255,255,255,0.1)] transition-colors disabled:opacity-50"
                           >
                             {refreshingTrack === track.trackKey ? (
                               <div className="animate-spin rounded-full h-2 w-2 border-b border-current"></div>
                             ) : (
                               <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                               </svg>
                             )}
                           </button>
                         </button>
                       ))}
                     </div>
                                         <div className="flex gap-2">
                       <button 
                         onClick={clearAllDiscoveries}
                         className="text-[rgba(255,255,255,0.5)] text-[11px] hover:text-white"
                       >
                         Clear All
                       </button>
                     </div>
                  </div>
                </div>
              )}

               {/* Playlist Tabs */}
               {playlistData.length > 0 && (
                 <div className="px-4 mb-4">
                   <div className="flex border-b border-[rgba(255,255,255,0.1)] overflow-x-auto scrollbar-hide">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                           <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#5856D5]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
               )}

               {/* Enhanced Sorting for Monitoring Data */}
               {playlistData.length > 0 && playlistData.some(p => p.dateAdded || p.daysInPlaylist !== undefined) && (
                 <div className="px-4 mb-4">
                   <div className="flex items-center justify-between text-xs">
                     <div className="flex items-center gap-4">
                       <span className="text-[rgba(255,255,255,0.7)]">Sort by:</span>
                       <select 
                         value={sortBy || 'followers'} // Changed default from 'dateAdded' to 'followers'
                         onChange={(e) => setSortBy(e.target.value)}
                         className="bg-[rgba(255,255,255,0.1)] text-white border border-[rgba(255,255,255,0.2)] rounded px-2 py-1 text-xs"
                       >
                         <option value="followers">Followers</option>
                         <option value="dateAdded">Date Added</option>
                         <option value="daysInPlaylist">Days in Playlist</option>
                         <option value="alphabetical">Alphabetical</option>
                       </select>
                       <button
                         onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                         className="text-[rgba(255,255,255,0.7)] hover:text-white transition-colors"
                         title={`Sort ${sortOrder === 'desc' ? 'Ascending' : 'Descending'}`}
                       >
                         {sortOrder === 'desc' ? '↓' : '↑'}
                       </button>
                     </div>
                     
                     <div className="text-[rgba(255,255,255,0.7)]">
                       Showing {sortedPlaylistData.length} playlists
                     </div>
                   </div>
                 </div>
               )}

               {/* Main Playlist Display */}
               <div className="px-4 flex-1 overflow-y-auto">
                 {/* Selected Track Indicator */}
                 {selectedDiscovery && (
                   <div className="mb-4 p-3 bg-[rgba(88,86,213,0.1)] border border-[rgba(88,86,213,0.3)] rounded-lg">
                     <div className="text-[#5856D5] text-[12px] font-semibold mb-1">
                       Showing playlists for:
                     </div>
                     <div className="text-white text-[11px]">
                       "{selectedDiscovery.song_title || selectedDiscovery.name}" by {selectedDiscovery.artist_name || selectedDiscovery.artist}
                     </div>
                   </div>
                 )}
                 
                 {/* Stats */}
                 {playlistData.length > 0 && (
                   <div className="flex items-center justify-between mb-4">
                     <span className="text-[rgba(255,255,255,0.5)] text-[10px] text-left">
                       {sortedPlaylistData.length} placement{sortedPlaylistData.length !== 1 ? 's' : ''}
                       {activeTab === 'all' && !selectedDiscovery && playlistData.length > 0 && (
                         <>
                           {' '}• {playlistData.filter(p => isOfficialPlaylist(p)).length} official, {' '}
                           {playlistData.filter(p => !isOfficialPlaylist(p)).length} user
                         </>
                       )}
                       {imagesLoading && (
                         <span className="text-[rgba(255,255,255,0.4)] text-[10px] ml-2">
                           • Loading cover art...
                         </span>
                       )}
                     </span>
                   </div>
                 )}

                 {/* Platform Breakdown (when multi-platform is enabled and has results) */}
                 {multiPlatformEnabled && playlistData.length > 0 && activeTab === 'all' && (
                   <div className="mb-4">
                     <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3">
                       <div className="text-white text-[11px] font-semibold mb-2">Platform Breakdown:</div>
                       <div className="grid grid-cols-2 gap-2">
                         {(() => {
                           const platformCounts = {};
                           playlistData.forEach(playlist => {
                             const platform = playlist.platform || 'unknown';
                             if (!platformCounts[platform]) {
                               platformCounts[platform] = 0;
                             }
                             platformCounts[platform]++;
                           });
                           
                           return Object.entries(platformCounts)
                             .sort(([,a], [,b]) => b - a) // Sort by count descending
                             .map(([platform, count]) => {
                               const platformInfo = availablePlatforms.find(p => p.id === platform);
                               const platformName = platformInfo?.name || platform;
                               const platformColor = platformInfo?.color || '#888888';
                               
                               return (
                                 <div key={platform} className="flex items-center justify-between">
                                   <div className="flex items-center gap-2">
                                     <div 
                                       className="w-2 h-2 rounded-full"
                                       style={{ backgroundColor: platformColor }}
                                     ></div>
                                     <span className="text-[rgba(255,255,255,0.8)] text-[10px]">
                                       {platformName}
                                     </span>
                                   </div>
                                   <span className="text-white text-[10px] font-semibold">
                                     {count}
                                   </span>
                                 </div>
                               );
                             });
                         })()}
                       </div>
                     </div>
                   </div>
                 )}

                {error && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-4">
                    <p className="text-yellow-400 text-[11px] text-left">{error}</p>
                  </div>
                )}

                {sortedPlaylistData.length === 0 && discoveryHistory.length === 0 ? (
                  <div className="py-[60px]">
                                         <h3 className="text-white text-[18px] font-bold mb-2 text-left">
                       No Discoveries Yet
                </h3>
                    <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 text-left">
                      Start by searching for tracks in your catalog to discover playlist placements.
                </p>
                <button 
                       onClick={() => setShowSearch(true)}
                       className="bg-[#5856D5] text-white px-5 py-2 rounded text-[12px] font-semibold hover:bg-[rgba(88,86,213,0.9)] transition-colors"
                >
                       Search Your First Track
                </button>
              </div>
                ) : sortedPlaylistData.length === 0 ? (
                  <div className="py-[40px]">
                    <p className="text-[rgba(255,255,255,0.5)] text-[12px] text-left">
                      No playlists found for the current filter.
                    </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {/* OFFICIAL PLAYLIST CARD STYLING - FULL CARD GREEN HIGHLIGHTING
                // ============================================================= 
                // Official playlists (Spotify-curated) get:
                // 1. Green border stroke (#22C55E) 
                // 2. Low opacity green background (rgba(34,197,94,0.08))
                // 3. Green hover effects for official playlist distinction
                // Non-official playlists keep the original white/purple styling
                */}
                {sortedPlaylistData.map((playlist, index) => (
                  <div 
                        key={playlist.track_id || index}
                        className={`${
                          isOfficialPlaylist(playlist)
                            ? 'bg-[rgba(34,197,94,0.08)] border border-[#22C55E] hover:border-[#22C55E] hover:bg-[rgba(34,197,94,0.12)]'
                            : 'bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] hover:border-[#5856D6] hover:bg-[rgba(88,86,214,0.05)]'
                        } rounded-lg p-4 transition-all cursor-pointer`}
                        onClick={() => {
                          if (playlist.playlist_url) {
                            window.open(playlist.playlist_url, '_blank');
                          }
                        }}
                  >
                    <div className="flex items-start gap-4">
                      {/* Playlist Icon */}
                                              <div 
                          ref={(el) => registerPlaylistElement(el, playlist)}
                          className="min-w-[48px] h-[48px] rounded-lg overflow-hidden flex items-center justify-center relative cursor-pointer"
                          style={{
                            width: '48px',
                            height: '48px',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                        {/* PLAYLIST COVER ART DISPLAY - VIEWPORT-BASED LAZY LOADING */}
                        {(() => {
                          const imageUrl = getPlaylistImageUrlEnhanced(playlist);
                          
                                                      if (imageUrl) {
                              // Show actual playlist cover art
                              return (
                                <img 
                                  src={imageUrl}
                                  alt={`${playlist.playlist_name} cover`}
                                  className="w-full h-full object-cover absolute inset-0"
                                  style={{
                                    minWidth: '48px',
                                    minHeight: '48px',
                                    maxWidth: '48px',
                                    maxHeight: '48px',
                                    objectFit: 'cover',
                                    objectPosition: 'center'
                                  }}
                                  onError={(e) => {
                                    // Fallback to gradient if image fails to load
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              );
                            }
                          
                          // Fallback to gradient with platform icon (shows while loading or if no image)
                          return (
                            <div className={`w-full h-full flex items-center justify-center`}
                                 style={{
                                   background: playlist.platform === 'spotify' 
                                     ? (isOfficialPlaylist(playlist) 
                                        ? 'linear-gradient(135deg, #22C55E, #16A34A)' 
                                        : 'linear-gradient(135deg, #5856D6, #7b69ff)')
                                     : `linear-gradient(135deg, ${availablePlatforms.find(p => p.id === playlist.platform)?.color || '#888888'}, ${availablePlatforms.find(p => p.id === playlist.platform)?.color || '#888888'}dd)`
                                 }}>
                              {playlist.platform === 'spotify' && (
                                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                                </svg>
                              )}
                              {playlist.platform === 'apple_music' && (
                                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                                </svg>
                              )}
                              {playlist.platform === 'amazon' && (
                                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595.383-.145.602-.072.602.22 0 .219-.174.4-.523.542-2.879 1.176-5.867 1.764-8.966 1.764-4.45 0-8.497-1.076-12.138-3.23-.248-.145-.36-.29-.36-.421 0-.073.029-.131.087-.175l.635-.25zm21.968.542c-.291-.073-.69.073-1.196.436l-1.107.796c-.218.145-.436.218-.655.218-.218 0-.436-.073-.654-.218-.51-.363-.763-.509-1.052-.436-.29.073-.436.291-.436.655v.218c0 .364.146.582.436.655.29.073.542-.073 1.052-.436.218-.145.436-.218.654-.218.219 0 .437.073.655.218l1.107.796c.506.363.905.509 1.196.436.29-.073.436-.291.436-.655v-.218c0-.364-.146-.582-.436-.655z"/>
                                </svg>
                              )}
                              {playlist.platform === 'youtube' && (
                                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                              )}
                              {playlist.platform === 'deezer' && (
                                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M18.81 8.86h2.915v1.31H18.81zm0 2.19h2.915v1.31H18.81zm0 2.19h2.915v1.31H18.81zm-3.65-4.38h2.915v1.31H15.16zm0 2.19h2.915v1.31H15.16zm0 2.19h2.915v1.31H15.16zm0 2.19h2.915v1.31H15.16zm-3.65-6.57h2.915v1.31H11.51zm0 2.19h2.915v1.31H11.51zm0 2.19h2.915v1.31H11.51zm0 2.19h2.915v1.31H11.51zm0 2.19h2.915v1.31H11.51z"/>
                                </svg>
                              )}
                            </div>
                          );
                        })()}
                        
                        {/* Error fallback - hidden by default, shown if image fails */}
                        <div 
                          className={`w-full h-full flex items-center justify-center absolute inset-0`}
                          style={{
                            display: 'none',
                            background: playlist.platform === 'spotify' 
                              ? (isOfficialPlaylist(playlist) 
                                 ? 'linear-gradient(135deg, #22C55E, #16A34A)' 
                                 : 'linear-gradient(135deg, #5856D6, #7b69ff)')
                              : `linear-gradient(135deg, ${availablePlatforms.find(p => p.id === playlist.platform)?.color || '#888888'}, ${availablePlatforms.find(p => p.id === playlist.platform)?.color || '#888888'}dd)`
                          }}>
                          {playlist.platform === 'spotify' && (
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                            </svg>
                          )}
                        </div>
                      </div>
                      
                      {/* Playlist Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-2 mb-1">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h3 className="text-white text-[14px] font-normal text-left">
                            {playlist.playlist_name || 'Unknown Playlist'}
                          </h3>
                                  {playlist.position && (
                                    <span className="text-[rgba(255,255,255,0.5)] text-[10px] bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded">
                            #{playlist.position}
                          </span>
                                  )}
                                </div>
                              </div>
                              {playlist.playlist_url && (
                                <div className="flex-shrink-0">
                                  <svg className="w-4 h-4 text-[rgba(255,255,255,0.5)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </div>
                              )}
                        </div>
                        
                            <p className="text-[rgba(255,255,255,0.5)] text-[12px] mb-1 text-left">
                            {playlist.curator || 'Unknown Curator'} • {availablePlatforms.find(p => p.id === playlist.platform)?.name || playlist.platform || 'Platform'}
                          </p>

                            <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-2 text-left">
                          {playlist.track_name || 'Unknown Track'} by {playlist.artist_name || 'Unknown Artist'}
                        </p>
                        
                        {/* Tags */}
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Platform Badge */}
                          {playlist.platform && (
                            <div 
                              className="px-2 py-1 rounded text-[10px] font-semibold flex items-center gap-1"
                              style={{
                                backgroundColor: `${availablePlatforms.find(p => p.id === playlist.platform)?.color || '#888888'}20`,
                                color: availablePlatforms.find(p => p.id === playlist.platform)?.color || '#888888'
                              }}
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                {playlist.platform === 'spotify' && (
                                  <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm4.586 14.424c-.18.295-.563.387-.857.207-2.348-1.435-5.304-1.76-8.785-.964-.335.077-.67-.133-.746-.47-.077-.334.132-.67.47-.745 3.809-.87 7.077-.496 9.713 1.115.294.18.386.563.205.857zm1.223-2.723c-.226.367-.706.482-1.073.257-2.687-1.652-6.785-2.131-9.965-1.166-.396.12-.813-.106-.933-.503-.12-.396.106-.813.503-.932 3.632-1.102 8.147-.568 11.22 1.33.367.225.482.705.248 1.014zm.105-2.835C13.018 6.95 7.56 6.777 4.4 7.69c-.464.134-.952-.134-1.085-.597-.134-.464.133-.952.596-1.085 3.613-1.043 9.758-.84 13.54 1.551.423.267.549.847.282 1.27-.267.424-.847.55-1.27.283z"/>
                                )}
                                {playlist.platform === 'apple_music' && (
                                  <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm3.5 6.5c0-.828-.672-1.5-1.5-1.5s-1.5.672-1.5 1.5.672 1.5 1.5 1.5 1.5-.672 1.5-1.5zm-6 8c-.828 0-1.5-.672-1.5-1.5v-5c0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5v5c0 .828-.672 1.5-1.5 1.5z"/>
                                )}
                                {playlist.platform === 'youtube' && (
                                  <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm3.5 10l-3 2v-4l3 2z"/>
                                )}
                                {/* Default music icon for other platforms */}
                                {!['spotify', 'apple_music', 'youtube'].includes(playlist.platform) && (
                                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                )}
                              </svg>
                              {availablePlatforms.find(p => p.id === playlist.platform)?.name || playlist.platform}
                            </div>
                          )}

                          {/* Date Added Information */}
                          {playlist.dateAdded && (
                            <div className="bg-[rgba(168,85,247,0.2)] px-2 py-1 rounded text-[10px] text-purple-400 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                              </svg>
                              {playlist.dateFormatted?.relative || 
                               (playlist.daysInPlaylist !== undefined ? 
                                (playlist.daysInPlaylist === 0 ? 'Added today' :
                                 playlist.daysInPlaylist === 1 ? 'Added yesterday' :
                                 `Added ${playlist.daysInPlaylist} days ago`) :
                                `Added ${formatRelativeTime(playlist.dateAdded)}`)}
                            </div>
                          )}

                          {/* Search Method Badge */}
                          {playlist.search_method === 'multi_platform_api' && (
                            <div className="bg-[rgba(34,197,94,0.2)] px-2 py-1 rounded text-[10px] text-green-400 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                              Multi-Platform
                            </div>
                          )}
                          
                          {/* Editorial Badge */}
                          {playlist.isEditorial && (
                            <div className="bg-[rgba(255,215,0,0.2)] px-2 py-1 rounded text-[10px] text-yellow-400 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              Editorial
                            </div>
                          )}

                          {/* Followers/Saves Badge - Only show for Spotify since other platforms' counts are inaccurate */}
                          {playlist.followers && playlist.platform === 'spotify' && (
                            <div className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded text-[10px] text-white">
                                  {playlist.followers} saves
                            </div>
                          )}
                          
                          {playlist.search_method === 'songstats_api' && (
                            <div className="bg-[rgba(255,165,0,0.2)] px-2 py-1 rounded text-[10px] text-orange-400 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                              SongStats
                            </div>
                          )}
                          
                              {/* Playlist Type Badge */}
                              {/* OFFICIAL PLAYLIST HIGHLIGHTING - GREEN BADGE UPDATE
                              // ================================================= 
                              // Official playlist badge now uses green (#22C55E) for clear distinction
                              // Official playlists are correctly identified by curator being "Spotify"
                              // Green coloring makes official playlists stand out prominently
                              */}
                              {isOfficialPlaylist(playlist) ? (
                                <div className="bg-[rgba(34,197,94,0.2)] px-2 py-1 rounded text-[10px] text-[#22C55E] flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  OFFICIAL
                                </div>
                              ) : (
                                <div className="bg-[rgba(88,86,214,0.2)] px-2 py-1 rounded text-[10px] text-purple-400 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                  </svg>
                                  User
                                </div>
                              )}
                          
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
            </>
          )}



        </div>
      </div>
    </LayoutHeader>
  );
};

export default PlaylistingApp; 